import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackrsFormValues, CareerCategory, setAccessToken, setCookie } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import {
  selectIsFeatureFlagEnabled,
  FEATURE_FLAGS,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useProcessInviteCodeForExistingUserMutation,
  usePostProfileOptionMutation,
  usePostUserMutation,
} from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { useProvisionPaymentSessionMutation } from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';
import { ENVIRONMENT } from '@backrs_ui/grow-ui/src/settings';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/loginHelper';
import { getGuestTokenIfPresent, getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { usePostData } from './hooks/usePostData';
import { OnboardingPage } from './OnboardingPage';
import { ONBOARDING_QUESTIONS } from './questions/questions.unified';
import { SignUpFormData } from './SignUpPage';
import { getVisibleSteps, getProgressStepIndex, VisibleStep } from './utils/getVisibleSteps';
import { parseName } from './utils/onboarding.utils';
import { StepKey, getNextStep } from './utils/stepNavigation';

type OnboardingView = 'questions' | 'subscription' | 'account-creation-loading' | 'invite-family';

export class OnboardingFormError extends Error {
  data: Record<string, string>;

  constructor(data: Record<string, string>) {
    super('Onboarding form validation error');
    this.name = 'OnboardingFormError';
    this.data = data;
  }
}

type OnboardingPageContainerProps = Readonly<{
  signUpData: SignUpFormData;
  activationCode: string | null;
}>;

export const OnboardingPageContainer = ({
  signUpData,
  activationCode,
}: OnboardingPageContainerProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.user);
  const isPaywallEnabled = useAppSelector(selectIsFeatureFlagEnabled(FEATURE_FLAGS.GROW_PAYWALL));
  const isFamilyAccountsEnabled = useAppSelector(
    selectIsFeatureFlagEnabled(FEATURE_FLAGS.FAMILY_ACCOUNTS),
  );

  const [currentView, setCurrentView] = useState<OnboardingView>('questions');

  const [currentStep, setCurrentStep] = useState<StepKey>('ROLE_SELECTION');
  const [storedFormData, setStoredFormData] = useState<BackrsFormValues | null>(
    activationCode ? { invite_code: activationCode } : null,
  );

  // Consolidated state management
  const [hasCompletedSubscription, setHasCompletedSubscription] = useState(false);
  const [hasCreatedUser, setHasCreatedUser] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [activationCodeValid, setActivationCodeValid] = useState<boolean | null>(null);
  const [accessMethod, setAccessMethod] = useState<
    'none' | 'activation_code' | 'paid_subscription' | 'family_membership'
  >('none');
  const [familyAccount, setFamilyAccount] = useState<{ id: number | null; seats: number } | null>(
    null,
  );

  // Track the step before entering special views for proper back navigation
  const entryPointStep = useRef<StepKey>('ROLE_SELECTION');

  // Helper function to transition to special views while saving the entry point
  const transitionToView = (newView: OnboardingView) => {
    // Save the current step as entry point before transitioning
    if (currentView === 'questions') {
      entryPointStep.current = currentStep;
    }

    setCurrentView(newView);
  };

  const questions = ONBOARDING_QUESTIONS();

  const [processInviteCodeForExistingUser] = useProcessInviteCodeForExistingUserMutation();
  const [postProfileOption] = usePostProfileOptionMutation();
  const [provisionPaymentSession] = useProvisionPaymentSessionMutation();
  const [postUser] = usePostUserMutation();

  useEffect(() => {
    // If user is authenticated and has completed subscription, redirect to home
    if (
      user?.access_token &&
      currentView !== 'account-creation-loading' &&
      currentView !== 'subscription' &&
      currentView !== 'invite-family' &&
      hasCompletedSubscription
    ) {
      navigate(APP_ROUTE.HOME);
    }
  }, [navigate, user?.access_token, currentView, hasCompletedSubscription]);

  const currentQuestion = questions[currentStep];
  const shouldSkipInviteFamily =
    !isFamilyAccountsEnabled || !isPaywallEnabled || accessMethod !== 'paid_subscription';

  const { handlePostProfileOption, handlePostUserActivity } = usePostData({
    questions,
    careerPathCategories: [] as CareerCategory[],
  });

  // Skip logic for conditional steps
  const shouldSkipStep = (stepKey: StepKey): boolean => {
    // COMPLETE_ACCOUNT is shown to everyone now
    // Fields within it are conditionally shown based on role via showIf

    // Skip SELECT_PLAN if paywall disabled OR activation code was valid
    if (stepKey === 'SELECT_PLAN' && (!isPaywallEnabled || activationCodeValid === true)) {
      return true;
    }

    // Skip INVITE_FAMILY if family_accounts disabled OR paywall disabled OR didn't pay via Stripe
    if (stepKey === 'INVITE_FAMILY' && shouldSkipInviteFamily) {
      return true;
    }

    return false;
  };

  // Get the next step key, accounting for skip logic
  const getNextStepKey = (current: StepKey): StepKey | null => {
    let nextKey = getNextStep(current);

    // Skip steps based on conditions
    while (nextKey && shouldSkipStep(nextKey)) {
      nextKey = getNextStep(nextKey);
    }

    return nextKey ?? null;
  };

  // Helper to show loading animation then navigate to home
  const navigateToHomeWithLoading = async () => {
    transitionToView('account-creation-loading');

    const existingToken = getBackrsToken();

    if (existingToken) {
      await handleAfterLogin({
        access_token: existingToken,
        dispatch,
        setErrorMessage: () => null,
      });

      // Track user activity - signals onboarding complete
      try {
        await handlePostUserActivity();
      } catch (error) {
        console.error('Failed to track user activity:', error);
        // Non-blocking - continue with flow even if this fails
      }
    }

    // Show loading screen for duration of all 3 messages (3.3s × 3 = 9.9s) to prevent repeat
    await new Promise((resolve) => setTimeout(resolve, 10000));
    navigate(APP_ROUTE.HOME);
  };

  const goToNextQuestion = async (formData: BackrsFormValues) => {
    // Store form data for later use
    setStoredFormData(formData);

    // Capture role selection at ROLE_SELECTION step
    let currentRole = selectedRole;

    if (currentStep === 'ROLE_SELECTION') {
      currentRole = formData.ROLE_SELECTION || null;

      // If role changed from student to non-student, clear academic fields from stored data
      if (selectedRole === 'student' && currentRole !== 'student') {
        const cleanedFormData = { ...formData };

        delete cleanedFormData.academicEnrollmentTypes;
        delete cleanedFormData.academicsCurrentEnrollment;
        delete cleanedFormData.academicProgramStartTerm;
        delete cleanedFormData.academicProgramStartYear;
        delete cleanedFormData.academicExpectedGraduationYear;
        setStoredFormData(cleanedFormData);
      }

      setSelectedRole(currentRole);
    }

    // After COMPLETE_ACCOUNT step, create the user account
    // This is where we have zip_code and can make the registration API call
    if (currentStep === 'COMPLETE_ACCOUNT' && !hasCreatedUser) {
      try {
        // Parse name into first_name and last_name
        const { first_name, last_name } = parseName(signUpData.name);

        // Combine signup data with onboarding data (including zip_code)
        const userData: BackrsFormValues = {
          first_name,
          last_name,
          phone: signUpData.phone,
          email: signUpData.email,
          password: signUpData.password,
          is_fourteen_plus: signUpData.is_fourteen_plus,
          zip_code: formData.zip_code,
          ...(currentRole && { onboarding_type: currentRole }),
        };

        const guestToken = getGuestTokenIfPresent();

        const { user: createdUser } = await postUser({
          userData,
          guestToken: guestToken ?? undefined,
        }).unwrap();

        if (createdUser?.access_token) {
          setAccessToken('backrs_ui_token', createdUser.access_token);
          setCookie(createdUser.access_token, ENVIRONMENT);
          setHasCreatedUser(true);

          // Check if user was auto-accepted into a family plan
          if (createdUser.familyMembership?.autoAccepted) {
            setAccessMethod('family_membership');
            // Skip directly to home - user has access through family membership
            await navigateToHomeWithLoading();

            return;
          }

          // Now that user is created, update onboarding_type on backend
          if (currentRole) {
            try {
              await postProfileOption({
                profileOptionAnswers: { onboardingType: currentRole },
                postKey: 'onboardingType',
              }).unwrap();
            } catch (error) {
              console.error('Failed to update onboarding type:', error);
              // Non-blocking - continue with flow even if this fails
            }
          }

          // Handle POST_PROFILE_OPTION action - post fields with post_type: 'PROFILE_OPTION'
          if (currentQuestion?.onComplete === 'POST_PROFILE_OPTION') {
            try {
              await handlePostProfileOption(formData, questions);
            } catch (error) {
              console.error('Failed to save profile options:', error);
              // Non-blocking - continue with flow even if this fails
            }
          }
        } else {
          throw new Error('User creation succeeded but no access token received');
        }
      } catch (error) {
        const apiError = error as { data?: Record<string, string> };

        if (apiError.data) {
          // Re-throw as OnboardingFormError so form can display the errors
          throw new OnboardingFormError(apiError.data);
        } else {
          throw new OnboardingFormError({
            general: 'An error occurred creating your account. Please try again.',
          });
        }
      }
    } else {
      // Handle POST_PROFILE_OPTION action for other steps
      if (currentQuestion?.onComplete === 'POST_PROFILE_OPTION') {
        try {
          await handlePostProfileOption(formData, questions);
        } catch (error) {
          console.error('Failed to save profile options:', error);
          // Non-blocking - continue with flow even if this fails
        }
      }
    }

    // Handle activation code validation at ACTIVATION_CODE step
    if (currentStep === 'ACTIVATION_CODE') {
      const inviteCode = formData.invite_code?.trim();

      if (inviteCode) {
        try {
          await processInviteCodeForExistingUser({ code: inviteCode }).unwrap();
          // Success - proceed to loading then dashboard
          setActivationCodeValid(true);
          setAccessMethod('activation_code');
          await navigateToHomeWithLoading();

          return;
        } catch (error) {
          // Failed - show error, don't navigate
          const errorData = error as { status?: number; data?: string | Record<string, string> };
          const errorMessage =
            errorData.data === 'invalid_invite_code'
              ? 'Invalid activation code. Please try again.'
              : typeof errorData.data === 'string'
                ? errorData.data
                : 'Invalid activation code. Please try again.';

          throw new OnboardingFormError({
            invite_code: errorMessage,
          });
        }
      }

      // No code entered (Skip was clicked)
      setActivationCodeValid(false);
      transitionToView('subscription');

      return;
    }

    // Calculate next step using skip logic
    const nextStep = getNextStepKey(currentStep);

    // Handle special transitions to subscription and invite-family views
    if (nextStep === 'SELECT_PLAN') {
      transitionToView('subscription');

      return;
    }

    if (nextStep === 'INVITE_FAMILY') {
      transitionToView('invite-family');

      return;
    }

    // If we have a next step, move to it
    if (nextStep) {
      setCurrentStep(nextStep);

      return;
    }

    // We've reached the end of questions - complete onboarding
    await navigateToHomeWithLoading();
  };

  const handleSubscriptionSuccess = async (sessionId?: string) => {
    setHasCompletedSubscription(true);
    setAccessMethod('paid_subscription');

    // Provision the payment session to finalize the subscription
    if (sessionId) {
      try {
        const result = await provisionPaymentSession({ sessionId }).unwrap();

        if (result.provision_status === 'success' && result.familyAccount) {
          // User has a family plan - show invite family step
          setFamilyAccount(result.familyAccount);
          setCurrentStep('INVITE_FAMILY');
          setCurrentView('invite-family');

          return;
        }
      } catch (error) {
        console.error('Failed to provision payment session:', error);
        // Non-blocking - continue with flow even if this fails
        // The backend webhook should handle subscription provisioning
      }
    }

    // Only show invite family if the feature is enabled
    if (isFamilyAccountsEnabled) {
      setCurrentStep('INVITE_FAMILY');
      setCurrentView('invite-family');
    } else {
      await navigateToHomeWithLoading();
    }
  };

  const handleBasicPlanContinue = async () => {
    setHasCompletedSubscription(true);
    await navigateToHomeWithLoading();
  };

  const handleInviteFamilyComplete = async () => {
    // TODO: Add logic to handle invite family complete
    await navigateToHomeWithLoading();
  };

  // Calculate visible steps for progress bar
  const visibleSteps: VisibleStep[] = getVisibleSteps({
    selectedRole,
    activationCodeValid,
    accessMethod,
    isPaywallEnabled,
    isFamilyAccountsEnabled,
  });

  const currentProgressStepIndex = getProgressStepIndex(visibleSteps, currentStep, currentView);

  // Handle progress bar step click - navigate to the clicked step
  const handleProgressStepClick = (stepIndex: number) => {
    // Block all back navigation after subscription is complete
    if (hasCompletedSubscription) return;

    const targetStep = visibleSteps[stepIndex];

    if (!targetStep) return;

    // Only allow clicking on completed (earlier) steps
    if (stepIndex >= currentProgressStepIndex) return;

    // Navigate based on the step type
    if (targetStep.key === 'SELECT_PLAN') {
      setCurrentView('subscription');
    } else if (targetStep.key === 'INVITE_FAMILY') {
      setCurrentView('invite-family');
    } else if (targetStep.key !== 'ACCOUNT_CREATION_LOADING') {
      setCurrentView('questions');
      setCurrentStep(targetStep.key as StepKey);
    }
  };

  return (
    <OnboardingPage
      currentQuestion={currentQuestion}
      goToNextQuestion={goToNextQuestion}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      isShowingAccountCreationLoading={currentView === 'account-creation-loading'}
      isShowingSubscription={currentView === 'subscription'}
      isShowingInviteFamily={currentView === 'invite-family'}
      storedFormData={storedFormData}
      onSubscriptionSuccess={handleSubscriptionSuccess}
      onBasicPlanContinue={handleBasicPlanContinue}
      onInviteFamilyComplete={handleInviteFamilyComplete}
      isPaywallEnabled={isPaywallEnabled}
      isFamilyAccountsEnabled={isFamilyAccountsEnabled}
      visibleSteps={visibleSteps}
      currentProgressStepIndex={currentProgressStepIndex}
      onProgressStepClick={handleProgressStepClick}
      canNavigateBack={!hasCompletedSubscription}
      familyAccountSeats={familyAccount?.seats}
    />
  );
};
